import { useCallback, useEffect, useState } from 'react';

import * as api from '@gitbook/api-client';
import { Icon } from '@gitbook/spine-icons';
import {
    Alert,
    Button,
    Description,
    Dialog,
    DialogBody,
    DialogCloseButton,
    DialogFooter,
    DialogHeader,
    FieldGroup,
    FilesDropZone,
    Heading,
    List,
    ListItem,
    ListItemBody,
    ListItemDescription,
    ListItemLeading,
    ListItemTrailing,
    LoadingIndicator,
    toast,
    useDialogAction,
} from '@gitbook/spine-react';
import {
    getContentTypeFromPath,
    MAX_IMPORT_FILE_SIZE,
    MAX_IMPORT_FILES,
    normalizeContentType,
    ZIP_MIME_TYPES,
} from '@gitbook/util-files';
import { useEventCallback } from '@gitbook/util-react-hooks';
import { assertNever } from '@gitbook/util-ts';

import { useSetAnalyticsScreen } from '../../navigation';
import { apiClient, useCurrentOrganizationId } from '../../state';
import { EnhanceSwitch, useEnhanceImportUpgrade } from './EnhanceSwitch';
import type { ImportSourceDialogProps } from './types';
import { useRunImport } from './util';

export function ImportFilesDialog(props: ImportSourceDialogProps): React.ReactElement {
    return (
        <Dialog>
            <DialogHeader>
                <Heading>Import Files</Heading>
                <Description>
                    Drop Markdown, HTML or ZIP files, to import them into GitBook.
                </Description>
            </DialogHeader>
            <ImportFilesForm {...props} />
        </Dialog>
    );
}

interface FileMeta {
    key: string | null;
    status: 'uploading' | 'uploaded';
}

const MAX_SIZE_MB = MAX_IMPORT_FILE_SIZE / (1024 * 1024);

function ImportFilesForm(props: ImportSourceDialogProps) {
    const { resolveTarget, onStart, trigger } = props;

    useSetAnalyticsScreen({ screen: 'import_start_source', source: 'file', trigger }, [trigger]);

    const [files, setFiles] = useState<Set<File>>(new Set());
    const [fileMeta, setFileMeta] = useState<Map<File, FileMeta>>(new Map());

    const error =
        files.size > MAX_IMPORT_FILES
            ? `You can’t upload more than ${MAX_IMPORT_FILES} files at once. To import more, either split your upload into smaller batches or compress your files into a ZIP archive.`
            : null;

    const deleteFile = useCallback((file: File) => {
        setFiles((existing) => {
            const copy = new Set(existing);
            copy.delete(file);
            return copy;
        });
        setFileMeta((existing) => {
            const copy = new Map(existing);
            copy.delete(file);
            return copy;
        });
    }, []);
    const getFileMeta = useEventCallback((file: File) => {
        return fileMeta.get(file);
    });
    const updateFileMeta = useCallback((file: File, meta: FileMeta) => {
        setFileMeta((existing) => {
            const copy = new Map(existing);
            copy.set(file, meta);
            return copy;
        });
    }, []);
    const organizationId = useCurrentOrganizationId();
    useEffect(() => {
        const uploadFile = async (file: File) => {
            if (!organizationId) {
                throw new Error('Missing organization for file import');
            }
            const type = getFileContentType(file);

            const { data } = await apiClient.storage.generateStorageUploadUrl(organizationId, {
                file: { name: file.name, size: file.size, type },
                kind: api.StorageUploadKind.ImportFile,
            });

            updateFileMeta(file, { key: data.key, status: 'uploading' });

            const response = await fetch(data.url, {
                method: 'PUT',
                body: file,
                headers: { 'Content-Type': type },
            });

            if (!response.ok) {
                throw new Error(`Failed to upload file: ${response.statusText}`);
            }

            updateFileMeta(file, { key: data.key, status: 'uploaded' });
        };
        files.forEach((file) => {
            if (!getFileMeta(file)) {
                uploadFile(file).catch((error) => {
                    deleteFile(file);
                    toast.error(`Failed to upload file ${file.name}`, error);
                });
            }
        });
    }, [files, organizationId, getFileMeta, updateFileMeta, deleteFile]);
    const isUploading = Array.from(files, (file) => fileMeta.get(file)).some(
        (meta) => !meta || meta.status === 'uploading'
    );
    const [isPending, startDialogAction] = useDialogAction();
    const runImport = useRunImport();

    const enhanceUpgrade = useEnhanceImportUpgrade();
    const [enhance, setEnhance] = useState(!enhanceUpgrade);

    const startImport = async () => {
        await startDialogAction(async ({ close }) => {
            await runImport({
                enhance: enhance && !enhanceUpgrade,
                trigger,
                resolveTarget,
                onStart: async (target) => {
                    await onStart?.(target);
                    await close();
                },
                source: {
                    type: 'file',
                    files: Array.from(files).map((file) => {
                        const meta = fileMeta.get(file);
                        if (!meta || meta.status !== 'uploaded' || !meta.key) {
                            throw new Error(`File ${file.name} is not uploaded`);
                        }
                        return meta.key;
                    }),
                },
            });
        });
    };
    const filesArray = Array.from(files);
    return (
        <>
            <DialogBody className="flex flex-col gap-4" scrollable={false}>
                <FieldGroup orientation="horizontal">
                    <EnhanceSwitch
                        value={enhance && !enhanceUpgrade}
                        onValueChange={setEnhance}
                        upgrade={enhanceUpgrade}
                    />
                </FieldGroup>
                <FilesDropZone
                    acceptedFileTypes="markdown|html|docx|zip"
                    onSelectFiles={(files) => {
                        const validFiles = files.reduce<File[]>((validFiles, file) => {
                            if (file.size > MAX_SIZE_MB * 1024 * 1024) {
                                toast.error(
                                    `File too large (maximum ${MAX_SIZE_MB}MB): ${file.name}`,
                                    null
                                );
                                return validFiles;
                            }
                            validFiles.push(file);
                            return validFiles;
                        }, []);
                        if (validFiles.length > 0) {
                            setFiles((existing) => new Set([...existing, ...validFiles]));
                        }
                    }}
                />
                {files.size > 0 ? (
                    <List
                        compact
                        bordered
                        className="max-h-52"
                        items={filesArray}
                        renderItem={({ item: file }) => {
                            return (
                                <ListItem
                                    key={`${file.name}-${file.lastModified}`}
                                    kind="bordered"
                                    size="large"
                                >
                                    <ListItemLeading>
                                        {(() => {
                                            const status =
                                                fileMeta.get(file)?.status ?? 'uploading';
                                            const FileIcon = getFileIcon(file);
                                            switch (status) {
                                                case 'uploading':
                                                    return <LoadingIndicator className="size-4" />;
                                                case 'uploaded':
                                                    return <FileIcon className="size-4" />;
                                                default:
                                                    assertNever(status);
                                            }
                                        })()}
                                    </ListItemLeading>
                                    <ListItemBody>
                                        <ListItemDescription>{file.name}</ListItemDescription>
                                    </ListItemBody>
                                    <ListItemTrailing when="always">
                                        <Button
                                            kind="blank"
                                            leadingIcon={Icon.Trashcan}
                                            label="Remove file"
                                            iconOnly
                                            onPress={() => deleteFile(file)}
                                        />
                                    </ListItemTrailing>
                                </ListItem>
                            );
                        }}
                    />
                ) : null}
                {error ? <Alert kind="danger" details={error} /> : null}
            </DialogBody>
            <DialogFooter>
                <DialogCloseButton />
                <Button
                    label="Start Import"
                    disabled={isPending || isUploading || files.size === 0 || Boolean(error)}
                    onPress={startImport}
                />
            </DialogFooter>
        </>
    );
}

function getFileContentType(file: File): string {
    return file.type ? normalizeContentType(file.type) : getContentTypeFromPath(file.name);
}

function getFileIcon(file: File) {
    const type = getFileContentType(file);
    if (ZIP_MIME_TYPES.includes(type)) {
        return Icon.FileZIP;
    }

    return Icon.FileCode;
}
